import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, FormCheck, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
// Context
import { MainContext } from "../../../App";
// Components
import FieldInput from "../../../components/global/form/FieldInput";
import FormSelect from "../../../components/global/form/FormSelect";
import TextLoading from "../../../components/global/TextLoading";
import ModalSuccess from "../../../components/global/modals/ModalSuccess";
// API
import { LocalCatalogues } from "../../../api/LocalCatalogues";
import {
  CreateFeatureFlag,
  DeleteFeatureFlag,
  UpdateFeatureFlag,
} from "../../../api/SuperAdmins";
// Constants
const prefix = process.env.REACT_APP_PREFIX;
const base = {
  id: null,
  name: "",
  description: "",
  isAvailable: false,
  isRelesed: false,
  organizations: [],
  roles: [],
  percentage: "",
};
const customStyles = {
  descriptionField: {
    resize: "none",
    height: "60px",
  },
};
export default function ModalSaveFeatureFlag(props) {
  const { show, onHide, setFeatureFlags, formatFeatureFlag } = props;
  const feature = props.feature || base;
  const [t] = useTranslation(["superAdmin", "global"]);
  const { permissions } = useContext(MainContext);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(show);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState("");

  // Validation schema for the formik form
  const validateSchema = Yup.object().shape({
    name: Yup.string().required(
      t("superAdmin:featureFlagsView.featureName") +
        " " +
        t("superAdmin:view.isRequired")
    ),
  });

  /**
   * Initial loading
   */
  useEffect(() => {
    getOrganizations();
    getRoles();
  }, []);

  /**
   * Description: Function to get organizations
   */
  const getOrganizations = () => {
    // Try to get user info from local storage
    const userInfo = JSON.parse(localStorage.getItem(`cmUserProfile${prefix}`));
    if (!userInfo) {
      setTimeout(() => getOrganizations(), 200);
      return;
    }
    // Filter organizations for plan_type 9 = schools
    const organizationsFiltered =
      userInfo?.organizations?.filter(
        (organization) => organization.plan_type_id == 9
      ) || [];
    // Format organizations for select
    const organizations = organizationsFiltered?.map((organization) => ({
      value: Number(organization.id),
      label: organization.name,
    }));
    setOrganizations(organizations);
  };

  /**
   * Description: Function to get roles
   */
  const getRoles = () => {
    const { allRoles } = LocalCatalogues();
    const roles = allRoles.map((role) => ({
      value: role.id,
      label: role.name,
    }));
    setRoles(roles);
  };

  /**
   * Description: Function to create a new feature flag
   * @param {object} values
   */
  const createFeature = (values) => {
    const payload = {
      name: values.name,
      description: values.description,
      is_active: 0,
      organization_ids: values.organizations?.map((org) => org.value),
      group_ids: values.roles?.map((role) => role.value),
    };
    if (values.percentage !== "") payload.percentage = values.percentage;
    CreateFeatureFlag(payload)
      .then((response) => {
        setIsSaving(false);
        if (response?.status) {
          const data = response.data || {};
          // Update the feature flags list
          setFeatureFlags((prevFlags) => [
            {
              ...formatFeatureFlag(data),
            },
            ...prevFlags,
          ]);
          // Close the modal
          onHide();
        } else {
          setError(response?.description);
        }
      })
      .finally(() => {});
  };

  /**
   * Description: Function to update an existing feature flag
   * @param {object} values
   */
  const updateFeature = (values) => {
    const payload = {
      id: values.id,
      description: values.description,
      is_active: values.isAvailable ? 1 : 0,
      is_active_globally: values.isRelesed ? 1 : 0,
      organization_ids: values.organizations?.map((org) => org.value),
      group_ids: values.roles?.map((role) => role.value),
    };
    if (values.percentage !== "") payload.percentage = values.percentage;
    UpdateFeatureFlag(payload)
      .then((response) => {
        setIsSaving(false);
        if (response?.status) {
          const data = response.data || {};
          // Update the feature flag in the list
          setFeatureFlags((prevFlags) => {
            const index = prevFlags.findIndex((flag) => flag.id == data.id);
            if (index === -1) return prevFlags;
            const updatedFlag = formatFeatureFlag(data);
            const newFlags = [...prevFlags];
            newFlags[index] = updatedFlag;
            return newFlags;
          });
          // Close the modal
          onHide();
        } else {
          setError(response?.description);
        }
      })
      .finally(() => {});
  };

  /**
   * Description: Function to delete the feature flag
   */
  const deleteFeature = () => {
    // Logic to delete the feature flag
    setIsSaving(true);
    const payload = {
      id: feature.id,
    };
    DeleteFeatureFlag(payload)
      .then((response) => {
        setIsSaving(false);
        if (response?.status) {
          // Update the feature flags list
          setFeatureFlags((prevFlags) =>
            prevFlags.filter((flag) => flag.id != feature.id)
          );
          setShowDeleteModal(false);
          onHide();
        } else {
          setError(response?.description);
        }
      })
      .finally(() => {});
  };

  return (
    <>
      <Modal show={showModal}>
        <Modal.Header>
          <Modal.Title>
            {feature?.id
              ? t("superAdmin:featureFlagsView.editFeature")
              : t("superAdmin:featureFlagsView.createFeature")}
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={feature}
          validationSchema={validateSchema}
          onSubmit={(values) => {
            setIsSaving(true);
            if (feature?.id) {
              updateFeature(values);
            } else {
              createFeature(values);
            }
          }}
        >
          {(formik) => (
            <Form>
              <Modal.Body>
                <Row>
                  <Col xs={12} className="mb-3">
                    <FieldInput
                      name="name"
                      label={t("superAdmin:featureFlagsView.featureName")}
                      type="text"
                      placeholder={t(
                        "superAdmin:featureFlagsView.featureNamePlaceholder"
                      )}
                      required={true}
                      maxLength={50}
                      disabled={feature?.id}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <FieldInput
                      label={t(
                        "superAdmin:featureFlagsView.featureDescription"
                      )}
                      name="description"
                      type="text"
                      as="textarea"
                      placeholder={t(
                        "superAdmin:featureFlagsView.featureDescriptionPlaceholder"
                      )}
                      style={customStyles.descriptionField}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <FormSelect
                      label={t("superAdmin:featureFlagsView.organizations")}
                      className={"multiplePillSelect"}
                      name="organizations"
                      options={organizations}
                      placeholder={t(
                        "superAdmin:featureFlagsView.organizationsPlaceholder"
                      )}
                      onChange={(event) => {
                        formik.setFieldValue("organizations", event);
                      }}
                      value={formik.values.organizations}
                      isMulti
                      isDisabled={formik.values.isRelesed}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <FormSelect
                      label={t("superAdmin:featureFlagsView.roles")}
                      className={"multiplePillSelect"}
                      name="roles"
                      options={roles}
                      placeholder={t(
                        "superAdmin:featureFlagsView.rolesPlaceholder"
                      )}
                      onChange={(event) => {
                        formik.setFieldValue("roles", event);
                      }}
                      value={formik.values.roles}
                      isMulti
                      isDisabled={formik.values.isRelesed}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="mb-3">
                    <FieldInput
                      label={t("superAdmin:featureFlagsView.percentage")}
                      name="percentage"
                      placeholder={t(
                        "superAdmin:featureFlagsView.percentagePlaceholder"
                      )}
                      type="number"
                      onInput={(e) => {
                        let value = e.target.value.replace(/\D/g, "");
                        if (value !== "") {
                          let num = Math.min(Number(value), 100);
                          value = num.toString();
                        }
                        e.target.value = value;
                        formik.setFieldValue(
                          "percentage",
                          value === "" ? "" : Number(value)
                        );
                      }}
                      value={formik.values.percentage}
                      disabled={formik.values.isRelesed}
                    />
                  </Col>
                </Row>
                {feature?.id && (
                  <Row>
                    <Col xs={12} className="mb-3">
                      <FormCheck
                        type="switch"
                        label={t("superAdmin:featureFlagsView.releaseFeature")}
                        name="isRelesed"
                        checked={formik.values.isRelesed}
                        onChange={(e) => {
                          formik.setFieldValue("isRelesed", e.target.checked);
                          // If the feature is released, clear organizations, roles and percentage
                          if (e.target.checked) {
                            formik.setFieldValue("organizations", []);
                            formik.setFieldValue("roles", []);
                            formik.setFieldValue("percentage", "");
                          } else {
                            formik.setFieldValue(
                              "organizations",
                              feature.organizations
                            );
                            formik.setFieldValue("roles", feature.roles);
                            formik.setFieldValue(
                              "percentage",
                              feature.percentage
                            );
                          }
                        }}
                      />
                    </Col>
                  </Row>
                )}
                {/**Errors */}
                {error && (
                  <Row>
                    <Col xs={12}>
                      <Alert variant="warning">{error}</Alert>
                    </Col>
                  </Row>
                )}
              </Modal.Body>
              <Modal.Footer
                className={feature?.id ? "justify-content-between" : ""}
              >
                {permissions.admin_feature_flags?.delete && feature?.id && (
                  <Button
                    variant="outline-danger"
                    onClick={() => {
                      setShowModal(false);
                      setShowDeleteModal(true);
                    }}
                    disabled={isSaving}
                  >
                    {t("global:buttons.eliminate")}
                  </Button>
                )}
                <div>
                  <Button
                    variant="outline-secondary"
                    onClick={onHide}
                    disabled={isSaving}
                  >
                    {t("global:buttons.cancel")}
                  </Button>
                  <Button type="submit" disabled={isSaving} variant="primary">
                    {isSaving ? (
                      <TextLoading
                        text={t(
                          feature?.id
                            ? "global:buttons.saving"
                            : "global:buttons.creating"
                        )}
                      />
                    ) : (
                      t(
                        feature?.id
                          ? "global:buttons.save"
                          : "global:buttons.create"
                      )
                    )}
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
      {showDeleteModal && (
        <ModalSuccess
          showModalSuccess={showDeleteModal}
          title={t("superAdmin:featureFlagsView.deleteFeature")}
          message={t("superAdmin:featureFlagsView.deleteFeatureMessage")}
          fnCancelButton={() => {
            setShowDeleteModal(false);
            setShowModal(true);
          }}
          loading={isSaving}
          fnAcceptButton={deleteFeature}
          txtBtnAccept={t("global:buttons.eliminate")}
          textLoading={t("global:buttons.eliminating")}
          error={error}
        />
      )}
    </>
  );
}

ModalSaveFeatureFlag.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  feature: PropTypes.object,
  setFeatureFlags: PropTypes.func,
  formatFeatureFlag: PropTypes.func,
};
